import React from "react";

/**
 * Same fruit/gourmand/salt split as admin-dashboard's ProductForm: fruit and
 * salt are judged by sweetness and coldness, gourmand by sweetness and
 * hardness — a dessert juice isn't "cold," it's heavy or light.
 */
const JUICE_TYPES = ["fruit", "gourmand", "salt"];
const JUICE_ATTRIBUTES = {
  fruit: ["sweetness", "coldness"],
  gourmand: ["sweetness", "hardness"],
  salt: ["sweetness", "coldness"],
};

const inputClass =
  "w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500";
const labelClass = "block text-sm font-medium text-gray-700 mb-2";

export const DEFAULT_JUICE_DETAILS = {
  juice_type: "fruit",
  sweetness_level: 3,
  coldness_level: 1,
  hardness_level: 2,
  pg_ratio: 40,
  vg_ratio: 60,
  bottle_size_ml: 30,
  available_nicotine_levels: [0, 3, 6],
};

export function parseJuiceDetails(raw) {
  const details = typeof raw === "string" ? JSON.parse(raw) : raw;
  return {
    ...details,
    sweetness_level: details.sweetness_level ?? 3,
    coldness_level: details.coldness_level ?? 1,
    hardness_level: details.hardness_level ?? 2,
  };
}

/** Drops the sliders that do not apply to the chosen juice type. */
export function serializeJuiceDetails(value) {
  const attributes = JUICE_ATTRIBUTES[value.juice_type] || [];
  return {
    ...value,
    sweetness_level: attributes.includes("sweetness") ? value.sweetness_level : null,
    coldness_level: attributes.includes("coldness") ? value.coldness_level : null,
    hardness_level: attributes.includes("hardness") ? value.hardness_level : null,
  };
}

/** The juice-specific fields shared by the product form and the range form. */
export default function JuiceDetailsFields({ value, onChange }) {
  return (
                  <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Juice details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Juice type</label>
              <select
                value={value.juice_type}
                onChange={(e) => onChange({ ...value, juice_type: e.target.value })}
                className={inputClass}
              >
                {JUICE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Bottle size (ml)</label>
              <input
                type="number"
                value={value.bottle_size_ml}
                onChange={(e) =>
                  onChange({ ...value, bottle_size_ml: parseInt(e.target.value, 10) })
                }
                className={inputClass}
              />
            </div>

            {JUICE_ATTRIBUTES[value.juice_type]?.includes("sweetness") && (
              <div>
                <label className={labelClass}>Sweetness ({value.sweetness_level})</label>
                <input
                  type="range"
                  min="0"
                  max="5"
                  value={value.sweetness_level}
                  onChange={(e) =>
                    onChange({ ...value, sweetness_level: parseInt(e.target.value, 10) })
                  }
                  className="w-full"
                />
              </div>
            )}

            {JUICE_ATTRIBUTES[value.juice_type]?.includes("coldness") && (
              <div>
                <label className={labelClass}>Coldness ({value.coldness_level})</label>
                <input
                  type="range"
                  min="0"
                  max="5"
                  value={value.coldness_level}
                  onChange={(e) =>
                    onChange({ ...value, coldness_level: parseInt(e.target.value, 10) })
                  }
                  className="w-full"
                />
              </div>
            )}

            {JUICE_ATTRIBUTES[value.juice_type]?.includes("hardness") && (
              <div>
                <label className={labelClass}>Hardness ({value.hardness_level})</label>
                <input
                  type="range"
                  min="0"
                  max="5"
                  value={value.hardness_level}
                  onChange={(e) =>
                    onChange({ ...value, hardness_level: parseInt(e.target.value, 10) })
                  }
                  className="w-full"
                />
              </div>
            )}

            <div>
              <label className={labelClass}>PG %</label>
              <input
                type="number"
                value={value.pg_ratio}
                onChange={(e) => onChange({ ...value, pg_ratio: parseInt(e.target.value, 10) })}
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>VG %</label>
              <input
                type="number"
                value={value.vg_ratio}
                onChange={(e) => onChange({ ...value, vg_ratio: parseInt(e.target.value, 10) })}
                className={inputClass}
              />
            </div>
          </div>
        </div>
  );
}
